import type * as Monaco from "monaco-editor";
import { KMainFunctions } from "../../../shared/utility/main-functions";

/** One language-server complaint, in the shape the toolbar and the drawer need. */
export interface ICodeEditorProblem {
  message: string;
  lineNumber: number;
  column: number;
  isError: boolean;
}

/**
 * Reads the markers Monaco currently holds for a model. The editors already publish
 * diagnostics; this is only the read side, so the toolbar count and the Messages tab
 * can never disagree with the squiggles.
 */
export const readModelProblems = (
  monacoInstance: typeof Monaco | undefined,
  model: Monaco.editor.ITextModel | null | undefined
): ICodeEditorProblem[] => {
  if (!monacoInstance || !model) return [];

  return monacoInstance.editor
    .getModelMarkers({ resource: model.uri })
    .filter(
      (marker: Monaco.editor.IMarker): boolean =>
        marker.severity === monacoInstance.MarkerSeverity.Error ||
        marker.severity === monacoInstance.MarkerSeverity.Warning
    )
    .map((marker: Monaco.editor.IMarker): ICodeEditorProblem => ({
      message: marker.message,
      lineNumber: marker.startLineNumber,
      column: marker.startColumn,
      isError: marker.severity === monacoInstance.MarkerSeverity.Error
    }));
};

/**
 * The text a failed run should keep. The same message the toast shows — the backend's own
 * words when it sent them, the transport error otherwise — so the two can never disagree.
 */
export const readRunErrorMessage = (error: unknown): string => {
  const apiMessage: string | undefined = KMainFunctions.getApiErrorMessage(error);
  if (apiMessage) return apiMessage;

  const message: string | undefined = (error as Error | undefined)?.message;
  return message ?? "The query could not be run.";
};
